import { ScheduledChargeNotificationMetadata } from 'state/slices/notificationsSlice';

/** Escape for safe use inside RegExp character class / alternation (keys are alphanumeric). */
function escapeRegExpKey(key: string): string {
  return key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * i18next interpolates `{{key}}` by default. System translations imported from CSV / AI often use
 * single `{key}` (see translations.service prompts). After `t()`, apply any leftover placeholders.
 * Always replace `{{key}}` before `{key}` so `{{chargeName}}` is not corrupted.
 */
export function applyKnownPlaceholders(template: string, values: Record<string, string>): string {
  let result = template;
  Object.keys(values).forEach((key) => {
    const val = values[key];
    const ke = escapeRegExpKey(key);
    result = result.replace(new RegExp(`\\{\\{\\s*${ke}\\s*\\}\\}`, 'g'), val);
  });
  Object.keys(values).forEach((key) => {
    const val = values[key];
    const ke = escapeRegExpKey(key);
    result = result.replace(new RegExp(`\\{\\s*${ke}\\s*\\}`, 'g'), val);
  });
  return result;
}

export function formatMetadataAmount(value: unknown): string {
  if (value == null || value === '') {
    return '';
  }
  return String(value);
}

/**
 * Billing notifications may store amounts on metadata.previousAmount/currentAmount and/or
 * amountDifference.oldValue/newValue. Normalize so callers can use one pair for branching and i18n.
 */
/**
 * Notifications persist the full ScheduledChargeNotification as JSON; charge fields usually live
 * under `.metadata`. Fall back to a flat object if inner metadata is empty/missing.
 */
export function resolveChargeMetadata(
  metadata: { metadata?: ScheduledChargeNotificationMetadata } & Record<string, unknown>,
): ScheduledChargeNotificationMetadata {
  const inner = metadata.metadata;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    const im = inner as ScheduledChargeNotificationMetadata;
    const innerHasChargeData =
      (im.chargeName != null && im.chargeName !== '') ||
      im.hasAmountChange !== undefined ||
      im.amountDifference != null;
    if (innerHasChargeData) {
      return im;
    }
  }
  return metadata as ScheduledChargeNotificationMetadata;
}

export function resolveChargeAmounts(m: ScheduledChargeNotificationMetadata): {
  previousAmount: string;
  currentAmount: string;
} {
  const diff = m?.amountDifference;
  const previousAmount = formatMetadataAmount(m?.previousAmount ?? diff?.oldValue);
  const currentAmount = formatMetadataAmount(m?.currentAmount ?? diff?.newValue);
  return { previousAmount, currentAmount };
}
